import React from 'react'
import { useRouteMatch } from 'react-router-dom'
import { routes } from '@sketch/constants'
import { useFlag } from 'containers/flags'

import {
  Wrapper,
  Title,
  Description,
  CallToActionWrapper,
  IconWrapper,
  PageFiller,
} from './EmptyState.styles'

// Icon
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-64-svg'
import { ReactComponent as LinkLibraryIcon } from '@sketch/icons/link-library-64-svg'
import { ReactComponent as PersonCircle } from '@sketch/icons/person-circle-64-svg'
import { ReactComponent as SearchIcon } from '@sketch/icons/search-64-svg'
import { ReactComponent as NotesIcon } from '@sketch/icons/notes.64-svg'
import { ReactComponent as TrashBinIcon } from '@sketch/icons/trash-bin-64-svg'
import { ReactComponent as WorkspaceIcon } from '@sketch/icons/workspace-64-svg'
import { ReactComponent as SymbolIcon } from '@sketch/icons/symbol-64-svg'
import { ReactComponent as LayerStyleIcon } from '@sketch/icons/paint-brush-64-svg'
import { ReactComponent as TextStyleIcon } from '@sketch/icons/text-format-64-svg'
import { ReactComponent as ColorVariableIcon } from '@sketch/icons/color-swatch-64-svg'
import { ReactComponent as UploadIcon } from '@sketch/icons/uploading-64-svg'
import { ReactComponent as TemplateIcon } from '@sketch/icons/template-64-svg'

const ICON_BY_NAME = {
  document: DocumentIcon,
  link: LinkLibraryIcon,
  search: SearchIcon,
  notes: NotesIcon,
  'person-circle': PersonCircle,
  'trash-bin': TrashBinIcon,
  workspace: WorkspaceIcon,
  symbol: SymbolIcon,
  layerStyle: LayerStyleIcon,
  textStyle: TextStyleIcon,
  colorVariable: ColorVariableIcon,
  upload: UploadIcon,
  template: TemplateIcon,
}

interface EmptyStateLayoutProps {
  title: React.ReactNode
  description: React.ReactNode
  callToAction?: React.ReactNode
  icon?: keyof typeof ICON_BY_NAME
}

const EmptyStateLayout: React.FC<EmptyStateLayoutProps> = props => {
  const { title, description, callToAction, icon } = props

  const Icon = ICON_BY_NAME[icon || 'document']

  return (
    <PageFiller>
      <Wrapper>
        <IconWrapper>
          <Icon />
          <span className="sr-only">{icon} icon</span>
        </IconWrapper>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <CallToActionWrapper>{callToAction}</CallToActionWrapper>
      </Wrapper>
    </PageFiller>
  )
}

interface EmptyStateProps extends EmptyStateLayoutProps {
  search?: string
}

const EmptyState: React.FC<EmptyStateProps> = ({ search, ...props }) => {
  const isDesignSystemsOn = useFlag('design-systems')

  const isWorkspaceLibrariesRoute = useRouteMatch({
    path: routes.WORKSPACE_LIBRARIES.template(),
    exact: true,
  })
  const isDesignSystems = useRouteMatch({
    path: routes.DESIGN_SYSTEM.template(),
  })

  if (search) {
    // if on design systems or on libraries, show a more specific message
    if (isDesignSystemsOn && (isWorkspaceLibrariesRoute || isDesignSystems)) {
      return (
        <EmptyStateLayout
          icon="search"
          title="No search results"
          description="No Libraries matched your search."
        />
      )
    }

    return (
      <EmptyStateLayout
        icon="search"
        title="No search results"
        description="No document or Editor names matched your search."
      />
    )
  }

  return <EmptyStateLayout {...props} />
}

export default EmptyState
